/**
 * Lobby UI Manager
 * Handles all lobby-related UI interactions and state display
 */

import { WebSocketConnection } from './websocket';
import { GameLobbyEntry, ClientLobbyState, DominoGameType } from './types';
import { TABLE_TEXTURES, getChairImage } from './assets';
// DATE: 2025-12-17 | Author: IA + Luiz Couto - Replaced Tippy.js with custom modal
// DATE: 2025-12-20 | Added PendingActionData for dynamic account creation context
import { getPlayerProfileModal, PlayerData, PendingActionData } from './components/PlayerProfileModal';

// DATE: 2025-12-17 | Author: IA + Luiz Couto - Added netcartasId for friend system
export interface LobbyScreenState {
  currentPlayer: {
    id: string;
    name: string;
    ranking?: number;
    gamesPlayed?: number;
    wins?: number;
    netcartasId?: number | null; // DATE: 2025-12-17 | NetCartas ID for friend system
  };
  lobbyState: ClientLobbyState;
  selectedGame: GameLobbyEntry | null;
}

export class LobbyUI {
  private lobbyWs: WebSocketConnection | null = null; // For lobby actions
  private state: LobbyScreenState;
  private welcomeSound: HTMLAudioElement;
  private serverUrl: string; // DATE: 2025-10-31 | Author: IA + Luiz Couto

  // DATE: 2025-12-18 | Author: IA + Luiz Couto
  // Callback to open private chat with a friend (injected from main.ts)
  private onOpenChat: ((netcartasId: number, nickname: string, isAI: boolean) => void) | null = null;

  constructor(serverUrl: string) {
    this.serverUrl = serverUrl;
    this.state = {
      currentPlayer: { id: '', name: '' },
      lobbyState: {
        games: [],
        players: [],
        stats: { totalActiveGames: 0, totalActivePlayers: 0, averageWaitTime: 0 },
      },
      selectedGame: null,
    };
    
    // Date: 2025-10-19 | Author: IA + Luiz Couto
    // Load welcome sound from local file (fixed CORS issue with R2 URLs)
    // Vite will handle this import and bundle it properly
    this.welcomeSound = new Audio(new URL('./soundfx/bemvindo.wav', import.meta.url).href);
    this.welcomeSound.volume = 0.6;
    this.welcomeSound.preload = 'auto'; // Preload the audio file
    this.welcomeSound.addEventListener('loadeddata', () => {
      console.log('✅ Welcome sound loaded successfully');
    });
    this.welcomeSound.addEventListener('error', (e) => {
      console.error('❌ Failed to load welcome sound:', e);
    });
    
    this.initializeEventListeners();
  }

  /**
   * Set the lobby WebSocket connection
   */
  public setLobbyWebSocket(lobbyWs: WebSocketConnection): void {
    this.lobbyWs = lobbyWs;
  }

  /**
   * DATE: 2025-12-18 | Author: IA + Luiz Couto
   * Set the callback to open private chat with a friend
   */
  public setOpenChatCallback(callback: (netcartasId: number, nickname: string, isAI: boolean) => void): void {
    this.onOpenChat = callback;
  }

  /**
   * Initialize all event listeners for lobby interactions
   * 
   * DATE: 2025-11-09 | Author: IA + Luiz Couto
   * Added debug logging for quick-match-btn
   */
  private initializeEventListeners(): void {
    const quickMatchBtn = document.getElementById('quick-match-btn');
    console.log('🔍 [DEBUG] Initializing event listeners...');
    console.log('🔍 [DEBUG] quick-match-btn element:', quickMatchBtn);
    
    quickMatchBtn?.addEventListener('click', () => {
      console.log('🖱️ [DEBUG] Quick match button CLICKED!');
      this.handleQuickMatch();
    });
    
    document.getElementById('create-game-btn')?.addEventListener('click', () => this.showCreateGameDialog());
    document.getElementById('cancel-queue-btn')?.addEventListener('click', () => this.handleCancelQueue());
    document.getElementById('filter-all-btn')?.addEventListener('click', () => this.filterGames('all'));
    document.getElementById('filter-available-btn')?.addEventListener('click', () => this.filterGames('available'));
    document.getElementById('filter-playing-btn')?.addEventListener('click', () => this.filterGames('playing'));
  }

  public setCurrentPlayer(player: LobbyScreenState['currentPlayer']): void {
    this.state.currentPlayer = player;
    this.updatePlayerInfoPanel();
  }

  public updateLobbyState(newState: ClientLobbyState): void {
    this.state.lobbyState = newState;
    this.updateGamesList();
    // this.updateLobbyStats(); // Disabled for now
  }

  /**
   * Update a single game in the list (real-time update)
   */
  public updateGame(game: GameLobbyEntry): void {
    const index = this.state.lobbyState.games.findIndex(g => g.id === game.id);
    
    if (index >= 0) {
      // Update existing game
      this.state.lobbyState.games[index] = game;
      // console.log('🔄 Updated game:', game.id);
    } else {
      // Add new game
      this.state.lobbyState.games.push(game);
      // console.log('➕ Added new game:', game.id);
    }
    
    // Update stats
    this.state.lobbyState.stats.totalActiveGames = this.state.lobbyState.games.length;
    
    // Re-render games list
    this.updateGamesList();
    this.updateLobbyStats();
  }

  /**
   * Remove a game from the list (real-time update)
   */
  public removeGame(gameId: string): void {
    const index = this.state.lobbyState.games.findIndex(g => g.id === gameId);
    
    if (index >= 0) {
      this.state.lobbyState.games.splice(index, 1);
      console.log('🗑️  Removed game:', gameId);
      
      // Update stats
      this.state.lobbyState.stats.totalActiveGames = this.state.lobbyState.games.length;
      
      // Re-render games list
      this.updateGamesList();
      this.updateLobbyStats();
    }
  }

  private updatePlayerInfoPanel(): void {
    const playerNameEl = document.getElementById('lobby-player-name');

    if (playerNameEl) {
      playerNameEl.textContent = this.state.currentPlayer.name;
    }

    // TODO: 2025-12-15 - Implement ranking and scoring system, then uncomment this block
    // const playerStatsEl = document.getElementById('lobby-player-stats');
    // if (playerStatsEl) {
    //   const stats = this.state.currentPlayer;
    //   playerStatsEl.innerHTML = `
    //     <div class="stat-item">
    //       <span class="stat-label">Jogos:</span>
    //       <span class="stat-value">${stats.gamesPlayed || 0}</span>
    //     </div>
    //     <div class="stat-item">
    //       <span class="stat-label">Vitórias:</span>
    //       <span class="stat-value">${stats.wins || 0}</span>
    //     </div>
    //     <div class="stat-item">
    //       <span class="stat-label">Ranking:</span>
    //       <span class="stat-value">${stats.ranking || 0}</span>
    //     </div>
    //   `;
    // }
  }

  private updateGamesList(): void {
    const gamesList = document.getElementById('games-list-container');
    if (!gamesList) return;

    if (this.state.lobbyState.games.length === 0) {
      gamesList.innerHTML = `
        <div class="empty-state">
          <p>Nenhuma partida ativa no momento</p>
          <p class="empty-state-hint">Clique em "Jogar Agora" para criar uma nova partida!</p>
        </div>
      `;
      return;
    }

    gamesList.innerHTML = this.state.lobbyState.games.map((game: GameLobbyEntry) => this.renderGameCard(game)).join('');
    this.setupGameListeners();
  }

  /**
   * Render game card with visual table and seated avatars
   * 
   * Date: 2025-11-02 | Author: IA + Luiz Couto
   * Shows table with players seated around it (2 or 4 seats depending on game type)
   */
  private renderGameCard(game: GameLobbyEntry): string {
    const playerCount = game.players.length;
    const isFull = playerCount >= game.maxPlayers;
    // const gameTypeLabel = this.getGameTypeLabel(game.type);

    // Date: 2025-11-02 | Author: IA + Luiz Couto
    // Create table visualization with seated players
    const tableHTML = this.renderTableWithSeats(game);

    // DATE: 2025-12-20 | Author: IA + Luiz Couto
    // Added game.id display on the right side of header for easy identification
    return `
      <div class="game-card" data-game-id="${game.id}">
        <div class="game-card-header">          
          <div class="game-status-badge ${game.status}">${this.getGameStatusLabel(game.status)}</div>
          <div class="game-id-label">${game.id}</div>
        </div>
        <div class="game-card-content">
            ${game.spectatorCount > 0 ? `
              <div class="game-stat-compact">
                <span class="stat-icon">👁️</span>
                <span class="stat-text">${game.spectatorCount}</span>
              </div>
            ` : ''}
          
          ${tableHTML}
        </div>
        <div class="game-card-actions">
          ${!isFull && game.status !== 'finished' ? `
            <button class="btn btn-primary join-game-btn" data-game-id="${game.id}">Entrar</button>
          ` : ''}
          ${game.allowSpectators ? `
            <button class="btn btn-secondary spectate-btn" data-game-id="${game.id}">Assistir</button>
          ` : ''}
        </div>
      </div>
    `;
  }

  /**
   * Render table with player seats (visual representation)
   * 
   * Date: 2025-11-02 | Author: IA + Luiz Couto
   * Creates a visual table with seats positioned around it using real chair images
   * Player overlays (avatar + name) are positioned on table edges, not on chairs
   */
  private renderTableWithSeats(game: GameLobbyEntry): string {
    const is2Player = game.maxPlayers === 2;
    const tableClass = is2Player ? 'table-2p' : 'table-4p';

    // Date: 2025-11-02 | Author: IA + Luiz Couto
    // Position mapping: 2-player (top/bottom), 4-player (top/right/bottom/left)
    const positions = is2Player 
      ? ['top', 'bottom']
      : ['top', 'right', 'bottom', 'left'];

    // Date: 2025-11-02 | Author: IA + Luiz Couto
    // Assign players to seats (fill from position 0)
    const seats = positions.map((position, index) => {
      const player = game.players[index];
      return {
        position,
        player: player || null,
        isEmpty: !player
      };
    });

    // Date: 2025-11-02 | Author: IA + Luiz Couto
    // Render player overlays separately (on table surface, not on chairs)
    const playerOverlays = seats
      .filter(seat => !seat.isEmpty)
      .map(seat => this.renderPlayerOverlay(seat.position, seat.player))
      .join('');

    return `
      <div class="table-visualization ${tableClass}">
        <div class="table-surface" style="background-image: url('${TABLE_TEXTURES.GREEN_FELT}');">
          ${playerOverlays}
        </div>
        ${seats.map(seat => this.renderChair(seat.position, seat.isEmpty, seat.player)).join('')}
      </div>
    `;
  }

  /**
   * Render a chair (empty or occupied)
   * 
   * Date: 2025-11-02 | Author: IA + Luiz Couto
   * Renders just the chair image - player info is rendered separately on the table
   * Updated: Also adds data-player attribute to occupied chairs for hover tooltip
   */
  private renderChair(position: string, isEmpty: boolean, player: any = null): string {
    // Date: 2025-11-02 | Author: IA + Luiz Couto
    // Get appropriate chair image (empty or occupied)
    const chairImage = getChairImage(
      position as 'top' | 'right' | 'bottom' | 'left',
      !isEmpty
    );

    const seatClass = isEmpty ? 'seat-empty' : 'seat-occupied';
    
    // Date: 2025-11-02 | Author: IA + Luiz Couto
    // Add player data to occupied chairs for tooltip functionality
    // DATE: 2025-12-17 | Author: IA + Luiz Couto - Added netcartasId and isAI for friend system
    let dataPlayerAttr = '';
    if (!isEmpty && player) {
      const playerData = JSON.stringify({
        id: player.id,
        name: player.name,
        connected: player.connected,
        profileImageURL: player.profileImageURL || null,
        ranking: player.ranking || 0,
        gamesPlayed: player.gamesPlayed || 0,
        wins: player.wins || 0,
        netcartasId: player.netcartasId || null, // DATE: 2025-12-17 | NetCartas ID for friends
        isAI: player.isAI || false // DATE: 2025-12-17 | Is this an AI player
      }).replace(/"/g, '&quot;');
      dataPlayerAttr = `data-player='${playerData}'`;
    }

    // Date: 2025-11-02 | Author: IA + Luiz Couto
    // Add small hotspot in center for tooltip trigger (avoids large transparent image areas)
    const hotspot = !isEmpty && player ? `<div class="seat-hotspot" ${dataPlayerAttr}></div>` : '';
    
    return `
      <div class="seat seat-${position} ${seatClass}">
        <img src="${chairImage}" alt="Cadeira" class="chair-image" />
        ${hotspot}
      </div>
    `;
  }

  /**
   * Render player overlay (name only) on table edge
   * 
   * Date: 2025-11-02 | Author: IA + Luiz Couto
   * Renders only player name on the table surface edge
   * Hover shows detailed popup with avatar, stats, and action buttons
   */
  private renderPlayerOverlay(position: string, player: any): string {
    const statusClass = player.connected ? 'connected' : 'disconnected';
    
    // Date: 2025-11-02 | Author: IA + Luiz Couto
    // Encode player data as JSON for popup
    // DATE: 2025-12-17 | Author: IA + Luiz Couto - Added netcartasId and isAI for friend system
    const playerData = JSON.stringify({
      id: player.id,
      name: player.name,
      connected: player.connected,
      profileImageURL: player.profileImageURL || null,
      ranking: player.ranking || 0,
      gamesPlayed: player.gamesPlayed || 0,
      wins: player.wins || 0,
      netcartasId: player.netcartasId || null, // DATE: 2025-12-17 | NetCartas ID for friends
      isAI: player.isAI || false // DATE: 2025-12-17 | Is this an AI player
    }).replace(/"/g, '&quot;');

    return `
      <div class="player-overlay player-overlay-${position} ${statusClass}" 
           data-player='${playerData}'>
        <div class="player-name-overlay">${player.name}</div>
      </div>
    `;
  }

  private setupGameListeners(): void {
    document.querySelectorAll('.join-game-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const gameId = (e.currentTarget as HTMLElement).getAttribute('data-game-id');
        if (gameId) this.joinGame(gameId, 'player');
      });
    });

    document.querySelectorAll('.spectate-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const gameId = (e.currentTarget as HTMLElement).getAttribute('data-game-id');
        if (gameId) this.joinGame(gameId, 'spectator');
      });
    });

    // Date: 2025-11-02 | Author: IA + Luiz Couto
    // Setup player click popups (changed from hover to click for better UX)
    this.setupPlayerPopups();
  }

  /**
   * Setup click popups for player info using Tippy.js
   * 
   * Date: 2025-11-02 | Author: IA + Luiz Couto
   * Click on player name or chair hotspot to open info popup
   * Popup stays open until clicking outside or clicking the element again
   */
  /**
   * Setup player profile popups using custom modal
   *
   * DATE: 2025-12-17 | Author: IA + Luiz Couto
   * Replaced Tippy.js with custom PlayerProfileModal for better control
   */
  private setupPlayerPopups(): void {
    // Select both player name overlays AND seat hotspots (clickable areas)
    const playerElements = document.querySelectorAll('.player-overlay, .seat-hotspot');

    // Get the modal instance and configure it
    const modal = getPlayerProfileModal();
    modal.setMyIdentity(
      this.state.currentPlayer?.netcartasId || null,
      this.state.currentPlayer?.name || ''
    );

    // Setup modal callbacks
    modal.setCallbacks({
      onSendMessage: (player) => {
        console.log(`💬 [2025-12-18] Send message to ${player.name} (${player.netcartasId})`);
        // DATE: 2025-12-18 | Author: IA + Luiz Couto
        // Open private chat with the friend
        if (player.netcartasId && this.onOpenChat) {
          this.onOpenChat(player.netcartasId, player.name, player.isAI || false);
        } else {
          console.error(`❌ [2025-12-18] Cannot open chat: no netcartasId or callback not set`);
        }
      },
      // DATE: 2025-12-20 | Author: IA + Luiz Couto
      // Callback dinâmico para criação de conta com ação pendente
      onCreateAccountWithAction: (pendingAction: PendingActionData) => {
        console.log(`📝 [2025-12-20] Create account requested with pending action:`, pendingAction.type);
        this.showCreateAccountModal(pendingAction);
      }
    });

    playerElements.forEach((element) => {
      const playerDataStr = element.getAttribute('data-player');
      if (!playerDataStr) return;

      // Remove existing click handler to prevent duplicates
      const existingHandler = (element as any).__profileClickHandler;
      if (existingHandler) {
        element.removeEventListener('click', existingHandler);
      }

      const playerData = JSON.parse(playerDataStr) as PlayerData;

      // Create click handler
      const clickHandler = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        console.log(`👆 [2025-12-17] Player clicked: ${playerData.name}`);
        modal.open(playerData);
      };

      // Store reference to handler for cleanup
      (element as any).__profileClickHandler = clickHandler;

      // Add click listener
      element.addEventListener('click', clickHandler);
    });

    console.log(`💡 [2025-12-17] Setup ${playerElements.length} player profile click handlers`);
  }

  /**
   * Show the account creation modal
   *
   * DATE: 2025-12-17 | Author: IA + Luiz Couto
   * DATE: 2025-12-20 | Refactored: Suporte a ações pendentes dinâmicas
   *
   * Shows a modal for unregistered users to create a NetCartas account.
   * After successful registration, executes the pending action automatically.
   *
   * @param pendingAction - Optional action to execute after registration (add_friend, view_ranking, etc.)
   */
  private showCreateAccountModal(pendingAction?: PendingActionData): void {
    console.log(`📝 [2025-12-20] Showing account creation modal`, pendingAction ? `with pending action: ${pendingAction.type}` : '');

    const modal = document.getElementById('registration-modal');
    const form = document.getElementById('registration-form') as HTMLFormElement;
    const nicknameInput = document.getElementById('reg-nickname') as HTMLInputElement;
    const emailInput = document.getElementById('reg-email') as HTMLInputElement;
    const passwordInput = document.getElementById('reg-password') as HTMLInputElement;
    const errorDiv = document.getElementById('registration-error') as HTMLDivElement;
    const submitBtn = document.getElementById('reg-submit-btn') as HTMLButtonElement;
    const cancelBtn = document.getElementById('reg-cancel-btn') as HTMLButtonElement;
    const subtitleEl = modal?.querySelector('.modal-subtitle') as HTMLElement;

    if (!modal || !form) {
      console.error('❌ [2025-12-20] Registration modal elements not found');
      this.showError('Erro ao abrir modal de registro');
      return;
    }

    // DATE: 2025-12-20 | Atualiza mensagem contextual baseada na ação pendente
    if (subtitleEl) {
      if (pendingAction?.contextMessage) {
        subtitleEl.textContent = `Crie sua conta gratuita ${pendingAction.contextMessage}!`;
      } else {
        subtitleEl.textContent = 'Crie sua conta gratuita para adicionar amigos e acessar mais recursos!';
      }
    }

    // Pre-fill nickname with current player name
    if (this.state.currentPlayer?.name) {
      nicknameInput.value = this.state.currentPlayer.name;
    }

    // Clear any previous errors
    errorDiv.classList.add('hidden');
    errorDiv.textContent = '';

    // Show modal
    modal.classList.remove('hidden');

    // Reference to this for closures
    const lobbyUI = this;

    // Handle form submission
    const handleSubmit = async (e: Event) => {
      e.preventDefault();
      console.log(`📝 [2025-12-20] Registration form submitted`);

      // Get form values
      const nickname = nicknameInput.value.trim();
      const email = emailInput.value.trim();
      const password = passwordInput.value;

      // Clear previous error
      errorDiv.classList.add('hidden');

      // Import validation functions
      const { validateNickname, validateEmail, validatePassword, createAccount } = await import('./api/accountApi');

      // Validate nickname
      const nicknameValidation = validateNickname(nickname);
      if (!nicknameValidation.valid) {
        errorDiv.textContent = nicknameValidation.error || 'Nome de usuário inválido';
        errorDiv.classList.remove('hidden');
        return;
      }

      // Validate email
      const emailValidation = validateEmail(email);
      if (!emailValidation.valid) {
        errorDiv.textContent = emailValidation.error || 'Email inválido';
        errorDiv.classList.remove('hidden');
        return;
      }

      // Validate password
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.valid) {
        errorDiv.textContent = passwordValidation.error || 'Senha inválida';
        errorDiv.classList.remove('hidden');
        return;
      }

      // Disable submit button and show loading state
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<span class="btn-spinner"></span> Criando conta...';

      try {
        // Call API to create account
        const result = await createAccount(nickname, email, password);

        if (result.success && result.userId) {
          console.log(`✅ [2025-12-20] Account created successfully, userId: ${result.userId}`);

          // Update current player's netcartasId
          if (lobbyUI.state.currentPlayer) {
            lobbyUI.state.currentPlayer.netcartasId = result.userId;
          }

          // Update PlayerProfileModal with new identity
          const profileModal = getPlayerProfileModal();
          profileModal.setMyIdentity(result.userId, nickname);
          console.log(`👤 [2025-12-20] Updated PlayerProfileModal identity after account creation`);

          // Hide modal
          modal.classList.add('hidden');
          cleanup();

          // Show success message
          lobbyUI.showSuccess('Conta criada com sucesso!');

          // DATE: 2025-12-20 | Execute pending action based on type
          if (pendingAction && result.userId) {
            await lobbyUI.executePendingAction(pendingAction, result.userId, nickname);
          }

        } else {
          // Show error
          errorDiv.textContent = result.error || 'Erro ao criar conta';
          errorDiv.classList.remove('hidden');

          // Re-enable submit button
          submitBtn.disabled = false;
          submitBtn.innerHTML = 'Criar Conta';
        }

      } catch (error) {
        console.error('❌ [2025-12-20] Error creating account:', error);
        errorDiv.textContent = 'Erro ao criar conta. Tente novamente.';
        errorDiv.classList.remove('hidden');

        // Re-enable submit button
        submitBtn.disabled = false;
        submitBtn.innerHTML = 'Criar Conta';
      }
    };

    // Handle cancel button
    const handleCancel = () => {
      modal.classList.add('hidden');
      cleanup();
    };

    // Cleanup function to remove event listeners
    const cleanup = () => {
      form.removeEventListener('submit', handleSubmit);
      cancelBtn.removeEventListener('click', handleCancel);
      // Reset form
      form.reset();
      submitBtn.disabled = false;
      submitBtn.innerHTML = 'Criar Conta';
    };

    // Add event listeners
    form.addEventListener('submit', handleSubmit);
    cancelBtn.addEventListener('click', handleCancel);
  }

  /**
   * Execute pending action after account creation
   *
   * DATE: 2025-12-20 | Author: IA + Luiz Couto
   *
   * Executes the action that was pending before account creation.
   * This provides a seamless UX where the user's original intent is fulfilled
   * automatically after creating an account.
   *
   * @param pendingAction - The action to execute
   * @param userId - The newly created user's ID
   * @param nickname - The user's nickname
   */
  private async executePendingAction(pendingAction: PendingActionData, userId: number, nickname: string): Promise<void> {
    console.log(`🎯 [2025-12-20] Executing pending action: ${pendingAction.type}`);

    switch (pendingAction.type) {
      case 'add_friend':
        // Adicionar amigo automaticamente após criar conta
        if (pendingAction.targetPlayer?.netcartasId) {
          console.log(`🤝 [2025-12-20] Sending friend invite to ${pendingAction.targetPlayer.name}`);
          try {
            const { sendFriendInvite } = await import('./api/friendsApi');
            const inviteResult = await sendFriendInvite(
              userId,
              pendingAction.targetPlayer.netcartasId,
              nickname
            );
            if (inviteResult.success) {
              // DATE: 2025-12-20 | Show feedback modal with visual confirmation
              this.showFeedbackModal(
                'Convite Enviado!',
                `Seu convite de amizade foi enviado para ${pendingAction.targetPlayer.name}. Aguarde a resposta!`,
                'success'
              );
            } else {
              console.warn(`⚠️ [2025-12-20] Friend invite response: ${inviteResult.error}`);
              this.showFeedbackModal(
                'Atenção',
                inviteResult.error || 'Não foi possível enviar o convite de amizade.',
                'error'
              );
            }
          } catch (err) {
            console.error('❌ [2025-12-20] Error sending friend invite:', err);
            this.showFeedbackModal(
              'Erro',
              'Ocorreu um erro ao enviar o convite de amizade. Tente novamente.',
              'error'
            );
          }
        }
        break;

      case 'view_ranking':
        // TODO: Implementar visualização de ranking
        console.log(`📊 [2025-12-20] View ranking action - to be implemented`);
        break;

      case 'save_stats':
        // TODO: Implementar salvamento de estatísticas
        console.log(`📈 [2025-12-20] Save stats action - to be implemented`);
        break;

      default:
        console.warn(`⚠️ [2025-12-20] Unknown pending action type: ${pendingAction.type}`);
    }
  }

  /**
   * Handle incoming friend request notification via WebSocket
   *
   * DATE: 2025-12-17 | Author: IA + Luiz Couto
   *
   * Shows a notification to the user that they received a friend request
   *
   * @param fromPlayerId - ID of the player who sent the request
   * @param fromPlayerName - Name of the player who sent the request
   * @param fromNetcartasId - NetCartas ID of the sender
   * @param saidId - The friendship invitation ID
   * @param isAI - Whether the sender is an AI player
   */
  public handleFriendRequestReceived(
    fromPlayerId: string,
    fromPlayerName: string,
    fromNetcartasId: number,
    saidId: number,
    isAI?: boolean
  ): void {
    console.log(`🤝 [2025-12-17] Friend request received from ${fromPlayerName} (${fromPlayerId})`);

    // Show notification with accept/decline options
    const notificationHtml = `
      <div class="friend-request-notification" data-player-id="${fromPlayerId}" data-netcartas-id="${fromNetcartasId}" data-said-id="${saidId}">
        <div class="notification-header">
          <span class="notification-icon">🤝</span>
          <span class="notification-title">Solicitação de Amizade</span>
        </div>
        <div class="notification-body">
          <strong>${fromPlayerName}</strong>${isAI ? ' (IA)' : ''} quer ser seu amigo!
        </div>
        <div class="notification-actions">
          <button class="btn btn-primary friend-accept-btn" data-action="accept">Aceitar</button>
          <button class="btn btn-secondary friend-decline-btn" data-action="decline">Recusar</button>
        </div>
      </div>
    `;

    // Create notification element
    const notificationEl = document.createElement('div');
    notificationEl.className = 'lobby-notification friend-request-popup';
    notificationEl.innerHTML = notificationHtml;

    // Add to notifications container (or create one if it doesn't exist)
    let container = document.getElementById('friend-notifications');
    if (!container) {
      container = document.createElement('div');
      container.id = 'friend-notifications';
      container.className = 'friend-notifications-container';
      document.body.appendChild(container);
    }
    container.appendChild(notificationEl);

    // Setup button handlers
    notificationEl.querySelector('.friend-accept-btn')?.addEventListener('click', () => {
      this.handleAcceptFriendRequest(fromPlayerId, fromNetcartasId, saidId);
      notificationEl.remove();
    });

    notificationEl.querySelector('.friend-decline-btn')?.addEventListener('click', () => {
      this.handleDeclineFriendRequest(fromPlayerId, fromNetcartasId, saidId);
      notificationEl.remove();
    });

    // Auto-dismiss after 30 seconds if not acted upon
    setTimeout(() => {
      if (notificationEl.parentNode) {
        notificationEl.remove();
      }
    }, 30000);
  }

  /**
   * Handle accepting a friend request
   *
   * DATE: 2025-12-17 | Author: IA + Luiz Couto
   */
  private async handleAcceptFriendRequest(fromPlayerId: string, fromNetcartasId: number, saidId: number): Promise<void> {
    console.log(`🤝 [2025-12-17] Accepting friend request from ${fromPlayerId}`);

    // Import respondToFriendInvite dynamically to avoid circular import issues
    const { respondToFriendInvite } = await import('./api/friendsApi');

    // DATE: 2025-12-22 | Need userId to call API
    const myNetcartasId = this.state.currentPlayer?.netcartasId;
    if (!myNetcartasId) {
      console.error('❌ [2025-12-22] Cannot accept friend request - missing netcartasId');
      this.showError('Erro ao aceitar amizade');
      return;
    }

    try {
      // Call API to accept the friend request
      // DATE: 2025-12-22 | Fixed: API requires (userId, friendId, saidId, accept, fromNickname)
      const result = await respondToFriendInvite(myNetcartasId, fromNetcartasId, saidId, true, fromPlayerId);

      if (result.success) {
        this.showSuccess('Amizade aceita!');

        // Send WebSocket notification
        if (this.lobbyWs) {
          this.lobbyWs.send({
            type: 'friendAccept',
            fromPlayerId,
            fromNetcartasId,
            saidId
          } as any);
        }
      } else {
        this.showError(result.error || 'Erro ao aceitar amizade');
      }
    } catch (error) {
      console.error('❌ [2025-12-17] Error accepting friend request:', error);
      this.showError('Erro ao aceitar amizade');
    }
  }

  /**
   * Handle declining a friend request
   *
   * DATE: 2025-12-17 | Author: IA + Luiz Couto
   */
  private async handleDeclineFriendRequest(fromPlayerId: string, fromNetcartasId: number, saidId: number): Promise<void> {
    console.log(`🤝 [2025-12-17] Declining friend request from ${fromPlayerId}`);

    // Import respondToFriendInvite dynamically to avoid circular import issues
    const { respondToFriendInvite } = await import('./api/friendsApi');

    // DATE: 2025-12-22 | Need userId to call API
    const myNetcartasId = this.state.currentPlayer?.netcartasId;
    if (!myNetcartasId) {
      console.error('❌ [2025-12-22] Cannot decline friend request - missing netcartasId');
      this.showError('Erro ao recusar solicitação');
      return;
    }

    try {
      // Call API to decline the friend request
      // DATE: 2025-12-22 | Fixed: API requires (userId, friendId, saidId, accept)
      const result = await respondToFriendInvite(myNetcartasId, fromNetcartasId, saidId, false);

      if (result.success) {
        this.showSuccess('Solicitação recusada');

        // Send WebSocket notification
        if (this.lobbyWs) {
          this.lobbyWs.send({
            type: 'friendDecline',
            fromPlayerId,
            fromNetcartasId,
            saidId
          } as any);
        }
      } else {
        this.showError(result.error || 'Erro ao recusar solicitação');
      }
    } catch (error) {
      console.error('❌ [2025-12-17] Error declining friend request:', error);
      this.showError('Erro ao recusar solicitação');
    }
  }

  /**
   * Handle notification that friend request was accepted
   *
   * DATE: 2025-12-17 | Author: IA + Luiz Couto
   */
  public handleFriendAccepted(_playerId: string, playerName: string, _netcartasId: number): void {
    console.log(`🤝 [2025-12-17] Friend request accepted by ${playerName}`);
    this.showSuccess(`${playerName} aceitou seu pedido de amizade!`);

    // TODO: Open private chat with the new friend (FASE 3)
    // Will use playerId and netcartasId to open chat
  }

  /**
   * Handle notification that friend request was declined
   *
   * DATE: 2025-12-17 | Author: IA + Luiz Couto
   */
  public handleFriendDeclined(playerId: string): void {
    console.log(`🤝 [2025-12-17] Friend request declined by ${playerId}`);
    this.showError('Sua solicitação de amizade foi recusada');
  }

  private updateLobbyStats(): void {
    const statsContainer = document.getElementById('lobby-stats');
    if (!statsContainer) return;

    const stats = this.state.lobbyState.stats;
    statsContainer.innerHTML = `
      <div class="stat-item">
        <span class="stat-label">Partidas ativas:</span>
        <span class="stat-value">${stats.totalActiveGames}</span>
      </div>
      <div class="stat-item">
        <span class="stat-label">Jogadores:</span>
        <span class="stat-value">${stats.totalActivePlayers}</span>
      </div>
      <div class="stat-item">
        <span class="stat-label">Espera média:</span>
        <span class="stat-value">${stats.averageWaitTime}s</span>
      </div>
    `;
  }

  private handleQuickMatch(): void {
    // DATE: 2025-11-09 | Author: IA + Luiz Couto
    // CRITICAL FIX: Add debug logging to diagnose quick match not working
    console.log('🎮 [DEBUG] handleQuickMatch() called');
    console.log('🎮 [DEBUG] lobbyWs exists?', !!this.lobbyWs);
    
    if (!this.lobbyWs) {
      console.error('❌ [DEBUG] lobbyWs is null! Cannot send quick match request');
      this.showError('Não conectado ao lobby');
      return;
    }
    
    console.log('✅ [DEBUG] lobbyWs is connected, proceeding with quick match');

    // Get preferences from selectors
    const gameTypeSelector = document.getElementById('game-type-selector') as HTMLSelectElement;
    const skillLevelSelector = document.getElementById('skill-level-selector') as HTMLSelectElement;

    // Date: 2025-11-05 | Author: IA + Luiz Couto
    // Date: 2025-12-26 | Changed default back to 'individual_2p' (Individual 1v1)
    const gameType = (gameTypeSelector?.value || 'individual_2p') as any;
    const skillLevel = (skillLevelSelector?.value || 'intermediate') as 'beginner' | 'intermediate' | 'advanced';

    const preferences = {
      gameTypes: [gameType],
      maxPlayers: 4,
      skillLevel,
      allowAI: false,
    };

    console.log('🎮 Requesting quick match with preferences:', preferences);
    this.lobbyWs.send({ type: 'quickMatch', preferences } as any);
    this.showQueueStatus();
  }

  /**
   * Show queue status (entering matchmaking)
   */
  private showQueueStatus(): void {
    const btn = document.getElementById('quick-match-btn') as HTMLButtonElement;
    const queueStatus = document.getElementById('queue-status');
    
    if (btn) {
      btn.classList.add('hidden');
    }
    if (queueStatus) {
      queueStatus.classList.remove('hidden');
    }
  }

  /**
   * Hide queue status (exiting matchmaking)
   */
  private hideQueueStatus(): void {
    const btn = document.getElementById('quick-match-btn') as HTMLButtonElement;
    const queueStatus = document.getElementById('queue-status');
    
    if (btn) {
      btn.classList.remove('hidden');
      btn.disabled = false;
    }
    if (queueStatus) {
      queueStatus.classList.add('hidden');
    }
  }

  /**
   * Update queue position and wait time
   */
  public updateQueueStatus(position: number, estimatedWait: number): void {
    const positionEl = document.getElementById('queue-position');
    const waitEl = document.getElementById('queue-wait');
    
    if (positionEl) {
      positionEl.textContent = `Posição: ${position}`;
    }
    if (waitEl) {
      waitEl.textContent = `Espera: ${estimatedWait}s`;
    }
  }

  /**
   * Handle cancel queue button
   */
  private handleCancelQueue(): void {
    if (!this.lobbyWs) return;
    
    console.log('❌ Cancelling queue');
    this.lobbyWs.send({ type: 'leaveQueue' } as any);
    this.hideQueueStatus();
  }

  /**
   * Handle match found
   */
  public onQuickMatchFound(gameId: string, gameType: string): void {
    this.hideQueueStatus();
    this.showSuccess('Partida encontrada! Conectando...');
    
    // Navigate to game after a short delay
    setTimeout(() => {
      this.navigateToGame(gameId, gameType);
    }, 500);
  }

  private showCreateGameDialog(): void {
    // For now, just create a room directly (can add modal later for advanced options)
    const event = new CustomEvent('createRoom');
    document.dispatchEvent(event);
  }

  private filterGames(filter: 'all' | 'available' | 'playing'): void {
    document.querySelectorAll('[id^="filter-"][id$="-btn"]').forEach(btn => {
      btn.classList.remove('active');
    });

    const activeBtn = document.getElementById(`filter-${filter}-btn`);
    if (activeBtn) activeBtn.classList.add('active');

    let filteredGames = this.state.lobbyState.games;
    
    if (filter === 'available') {
      filteredGames = filteredGames.filter(g => 
        g.status === 'waiting' && g.players.length < g.maxPlayers
      );
    } else if (filter === 'playing') {
      filteredGames = filteredGames.filter(g => g.status === 'playing');
    }

    const gamesList = document.getElementById('games-list-container');
    if (gamesList) {
      gamesList.innerHTML = filteredGames.map((game: GameLobbyEntry) => this.renderGameCard(game)).join('') || `
        <div class="empty-state">
          <p>Nenhuma partida encontrada com este filtro</p>
        </div>
      `;
      this.setupGameListeners();
    }
  }

  private joinGame(gameId: string, role: 'player' | 'spectator'): void {
    // 2025-10-28: Navigate directly to game room with role
    // No need to go through lobby - just connect as player or spectator
    console.log(`🎯 [2025-10-28] Navigating to game ${gameId} as ${role}`);
    this.navigateToGame(gameId, undefined, role);
  }

  private navigateToGame(gameId: string, gameType?: string, role: 'player' | 'spectator' = 'player'): void {
    const event = new CustomEvent('navigateToGame', { detail: { gameId, gameType, role } });
    document.dispatchEvent(event);
  }

  /**
   * Play welcome sound
   */
  public playWelcomeSound(): void {
    this.welcomeSound.currentTime = 0;
    this.welcomeSound.play().catch(err => {
      console.warn('Could not play welcome sound:', err);
    });
    console.log('🔊 Playing welcome sound');
  }

  public showLobbyScreen(): void {
    console.log('🏠 Showing lobby screen');
    const lobbyScreen = document.getElementById('lobby-screen');
    const gameScreen = document.getElementById('game-screen');
    if (lobbyScreen) lobbyScreen.classList.add('active');
    if (gameScreen) gameScreen.classList.remove('active');
  }

  public hideLobbyScreen(): void {
    const lobbyScreen = document.getElementById('lobby-screen');
    if (lobbyScreen) lobbyScreen.classList.remove('active');
  }

  /**
   * Get display label for game type
   * 
   * DATE: 2025-10-31 | Author: IA + Luiz Couto
   * Simplified: Only 2 game modes
   * - individual_2p = 2 players (1v1)
   * - partnerships_4p = 4 players (2v2)
   */
  private getGameTypeLabel(type: DominoGameType): string {
    const labels: Record<DominoGameType, string> = {
      [DominoGameType.INDIVIDUAL_2P]: 'Individual',
      [DominoGameType.PARTNERSHIPS_4P]: 'Duplas',
      // Future game types (not implemented yet)
      [DominoGameType.AI_MIXED]: 'Com IA',
      [DominoGameType.AI_TRAINING]: 'Treino IA',
    };
    return labels[type] || type;
  }

  private getGameStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      'waiting': 'Aguardando',
      'playing': 'Jogando',
      'finished': 'Finalizado',
    };
    return labels[status] || status;
  }

  /**
   * Show error message in the appropriate container based on current tab
   * DATE: 2025-12-21 | Updated to support admin tab alerts
   */
  public showError(message: string): void {
    const alertEl = document.createElement('div');
    alertEl.className = 'lobby-alert alert-error';
    alertEl.textContent = message;

    // DATE: 2025-12-21 | Use admin-alerts if on admin tab, otherwise lobby-alerts
    const containerId = this._currentAdminTab === 'admin' ? 'admin-alerts' : 'lobby-alerts';
    const container = document.getElementById(containerId);
    if (container) {
      container.appendChild(alertEl);
      setTimeout(() => alertEl.remove(), 5000);
    } else {
      // Fallback: log to console if no container found
      console.error('❌ Alert container not found:', containerId, message);
    }
  }

  /**
   * Show success message in the appropriate container based on current tab
   * DATE: 2025-12-21 | Updated to support admin tab alerts
   */
  public showSuccess(message: string): void {
    const alertEl = document.createElement('div');
    alertEl.className = 'lobby-alert alert-success';
    alertEl.textContent = message;

    // DATE: 2025-12-21 | Use admin-alerts if on admin tab, otherwise lobby-alerts
    const containerId = this._currentAdminTab === 'admin' ? 'admin-alerts' : 'lobby-alerts';
    const container = document.getElementById(containerId);
    if (container) {
      container.appendChild(alertEl);
      setTimeout(() => alertEl.remove(), 3000);
    } else {
      // Fallback: log to console if no container found
      console.log('✅ Alert container not found:', containerId, message);
    }
  }

  /**
   * Shows a prominent feedback modal with visual feedback (success/error)
   * 
   * DATE: 2025-12-20 | Author: IA + Luiz Couto
   * 
   * This modal provides clear visual feedback to the user after important actions
   * like sending a friend invite. It follows the same design language as the
   * registration modal.
   *
   * @param title - Title of the feedback message
   * @param message - Detailed message to display
   * @param type - 'success' or 'error' to determine styling
   */
  public showFeedbackModal(title: string, message: string, type: 'success' | 'error' = 'success'): void {
    console.log(`📢 [2025-12-20] Showing feedback modal: ${type} - ${title}`);

    const modal = document.getElementById('feedback-modal');
    const modalContent = modal?.querySelector('.modal-feedback');
    const iconContainer = document.getElementById('feedback-icon');
    const titleEl = document.getElementById('feedback-title');
    const messageEl = document.getElementById('feedback-message');
    const okBtn = document.getElementById('feedback-ok-btn');

    if (!modal || !iconContainer || !titleEl || !messageEl || !okBtn) {
      console.error('❌ [2025-12-20] Feedback modal elements not found');
      // Fallback to simple showSuccess/showError
      if (type === 'success') {
        this.showSuccess(message);
      } else {
        this.showError(message);
      }
      return;
    }

    // Configure icon based on type
    if (type === 'success') {
      iconContainer.innerHTML = '<span class="feedback-icon-success">✓</span>';
      modalContent?.classList.remove('feedback-error');
    } else {
      iconContainer.innerHTML = '<span class="feedback-icon-error">✕</span>';
      modalContent?.classList.add('feedback-error');
    }

    // Set content
    titleEl.textContent = title;
    messageEl.textContent = message;

    // Show modal
    modal.classList.remove('hidden');

    // Handle OK button
    const handleOk = () => {
      modal.classList.add('hidden');
      okBtn.removeEventListener('click', handleOk);
    };

    okBtn.addEventListener('click', handleOk);

    // Also close on background click
    const handleBackgroundClick = (e: MouseEvent) => {
      if (e.target === modal) {
        modal.classList.add('hidden');
        modal.removeEventListener('click', handleBackgroundClick);
      }
    };

    modal.addEventListener('click', handleBackgroundClick);
  }

  /**
   * Check if admin mode is enabled via URL parameter
   * 
   * DATE: 2025-10-31 | Author: IA + Luiz Couto
   * 
   * Checks if URL contains ?admin=true to show admin panel
   */
  public isAdminMode(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('admin') === 'true';
  }

  // DATE: 2025-12-21 | Track current admin tab view (used for state tracking)
  private _currentAdminTab: 'lobby' | 'admin' = 'lobby';

  /** Get current admin tab state */
  public get currentAdminTab(): 'lobby' | 'admin' {
    return this._currentAdminTab;
  }

  /**
   * Initialize admin panel if admin mode is enabled
   *
   * DATE: 2025-10-31 | Author: IA + Luiz Couto
   * DATE: 2025-12-21 | Redesigned with tab-based navigation
   *
   * Called after lobby screen is shown to display admin controls
   */
  public initializeAdminPanel(): void {
    if (!this.isAdminMode()) {
      console.log('🔧 Admin mode not enabled');
      return;
    }

    console.log('🔧 [2025-12-21] Admin mode enabled, initializing tab system');

    // Add admin tabs to header
    this.createAdminTabs();

    // Create admin screen container (hidden by default)
    this.createAdminScreen();

    // Set up tab click handlers
    this.setupAdminTabListeners();
  }

  /**
   * Create admin tabs in the lobby header
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private createAdminTabs(): void {
    const header = document.querySelector('.lobby-header');
    if (!header) return;

    // Check if tabs already exist
    if (document.getElementById('admin-tabs')) return;

    // Create tabs container
    const tabsContainer = document.createElement('div');
    tabsContainer.id = 'admin-tabs';
    tabsContainer.className = 'admin-tabs';
    tabsContainer.innerHTML = `
      <button class="admin-tab active" data-tab="lobby">
        <span class="tab-icon">🎮</span>
        <span class="tab-label">Lobby</span>
      </button>
      <button class="admin-tab" data-tab="admin">
        <span class="tab-icon">🔧</span>
        <span class="tab-label">Admin</span>
      </button>
    `;

    // Insert after branding, before user menu
    const userMenu = header.querySelector('.lobby-user-menu');
    if (userMenu) {
      header.insertBefore(tabsContainer, userMenu);
    } else {
      header.appendChild(tabsContainer);
    }
  }

  /**
   * Create the admin screen container
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private createAdminScreen(): void {
    const lobbyScreen = document.getElementById('lobby-screen');
    if (!lobbyScreen) return;

    // Check if already exists
    if (document.getElementById('admin-screen')) return;

    // Create admin screen
    const adminScreen = document.createElement('div');
    adminScreen.id = 'admin-screen';
    adminScreen.className = 'admin-screen hidden';

    // Insert after header, alongside lobby-main
    const lobbyMain = lobbyScreen.querySelector('.lobby-main');
    if (lobbyMain) {
      lobbyMain.parentNode?.insertBefore(adminScreen, lobbyMain.nextSibling);
    }

    // Render admin content
    this.renderAdminScreen(adminScreen);
  }

  /**
   * Setup tab click listeners
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private setupAdminTabListeners(): void {
    const tabs = document.querySelectorAll('.admin-tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const tabName = target.dataset.tab as 'lobby' | 'admin';
        this.switchAdminTab(tabName);
      });
    });
  }

  /**
   * Switch between lobby and admin tabs
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private switchAdminTab(tab: 'lobby' | 'admin'): void {
    console.log(`🔧 [2025-12-21] Switching to tab: ${tab}`);
    this._currentAdminTab = tab;

    // Update tab active states
    const tabs = document.querySelectorAll('.admin-tab');
    tabs.forEach(t => {
      t.classList.toggle('active', t.getAttribute('data-tab') === tab);
    });

    // Toggle visibility of lobby-main and admin-screen
    const lobbyMain = document.querySelector('.lobby-main') as HTMLElement;
    const adminScreen = document.getElementById('admin-screen');
    const lobbyFooter = document.querySelector('#lobby-screen > footer') as HTMLElement;

    if (lobbyMain) lobbyMain.classList.toggle('hidden', tab === 'admin');
    if (adminScreen) adminScreen.classList.toggle('hidden', tab === 'lobby');
    if (lobbyFooter) lobbyFooter.classList.toggle('hidden', tab === 'admin');

    // Refresh admin data when switching to admin tab
    if (tab === 'admin') {
      this.refreshAdminData();
    }
  }

  /**
   * Render the admin screen with organized cards
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private renderAdminScreen(container: HTMLElement): void {
    container.innerHTML = `
      <div class="admin-container">
        <div class="admin-header">
          <h2>🔧 Painel de Administração</h2>
          <p class="admin-subtitle">Gerencie jogos, IA e configurações do sistema</p>
        </div>

        <!-- DATE: 2025-12-21 | Container de alertas para a aba admin -->
        <div id="admin-alerts" class="lobby-alerts admin-alerts"></div>

        <div class="admin-grid">
          <!-- Card: Jogos de IA -->
          <div class="admin-card admin-card-games">
            <div class="admin-card-header">
              <div class="admin-card-icon">🤖</div>
              <h3>Jogos de IA</h3>
              <button id="admin-refresh-btn" class="admin-icon-btn" title="Atualizar lista">
                <span>↻</span>
              </button>
            </div>
            <div class="admin-card-content">
              <div class="admin-create-buttons">
                <button id="admin-create-ai-2p-btn" class="admin-btn admin-btn-create">
                  <span class="btn-icon">➕</span>
                  <span>Individual (2P)</span>
                </button>
                <button id="admin-create-ai-4p-btn" class="admin-btn admin-btn-create">
                  <span class="btn-icon">➕</span>
                  <span>Duplas (4P)</span>
                </button>
              </div>
              <div id="admin-ai-games-list" class="admin-games-list">
                <div class="admin-loading">Carregando...</div>
              </div>
            </div>
          </div>

          <!-- Card: Controles de Reset -->
          <div class="admin-card admin-card-danger">
            <div class="admin-card-header">
              <div class="admin-card-icon">⚠️</div>
              <h3>Controles de Reset</h3>
            </div>
            <div class="admin-card-content">
              <div class="admin-danger-zone">
                <button id="admin-reset-all-btn" class="admin-btn admin-btn-danger">
                  <span class="btn-icon">🗑️</span>
                  <span>Resetar Jogos de IA</span>
                </button>
                <p class="admin-hint">Remove apenas jogos com jogadores IA</p>

                <div class="admin-divider"></div>

                <button id="admin-force-reset-btn" class="admin-btn admin-btn-critical">
                  <span class="btn-icon">⚡</span>
                  <span>Forçar Reset TODOS</span>
                </button>
                <p class="admin-hint admin-hint-danger">Remove TODOS os jogos em andamento</p>

                <div class="admin-divider"></div>

                <button id="admin-clean-finished-btn" class="admin-btn admin-btn-warning">
                  <span class="btn-icon">🧹</span>
                  <span>Limpar Finalizadas</span>
                </button>
                <p class="admin-hint">Remove jogos com status "finalizado" (fantasmas)</p>
              </div>
            </div>
          </div>

          <!-- Card: Ghost Rooms -->
          <div class="admin-card admin-card-ghost">
            <div class="admin-card-header">
              <div class="admin-card-icon">👻</div>
              <h3>Ghost Rooms</h3>
            </div>
            <div class="admin-card-content">
              <button id="admin-show-ghosts-btn" class="admin-btn admin-btn-ghost">
                <span class="btn-icon">🔍</span>
                <span>Detectar Salas Fantasma</span>
              </button>
              <div id="admin-ghost-results" class="admin-ghost-results"></div>
            </div>
          </div>

          <!-- Card: AICompanion Test -->
          <div class="admin-card admin-card-companion admin-card-wide">
            <div class="admin-card-header">
              <div class="admin-card-icon">📧</div>
              <h3>AICompanion - Re-engagement Email</h3>
            </div>
            <div class="admin-card-content">
              <div class="companion-test-form">
                <div class="companion-form-row">
                  <div class="companion-form-group">
                    <label>Companion</label>
                    <select id="admin-companion-select" class="admin-select">
                      <option value="Lisa">Lisa</option>
                      <option value="Rex">Rex</option>
                      <option value="Dudu">Dudu</option>
                    </select>
                  </div>
                  <div class="companion-form-group">
                    <label>Friend ID</label>
                    <input type="number" id="admin-friend-id" class="admin-input" placeholder="Opcional" />
                  </div>
                  <div class="companion-form-group">
                    <label>Delay (s)</label>
                    <input type="number" id="admin-delay-secs" class="admin-input" value="60" />
                  </div>
                </div>
                <div class="companion-actions">
                  <button id="admin-companion-status-btn" class="admin-btn admin-btn-info">
                    <span>📊</span> Status
                  </button>
                  <button id="admin-companion-schedule-btn" class="admin-btn admin-btn-primary">
                    <span>⏰</span> Agendar
                  </button>
                  <button id="admin-companion-send-now-btn" class="admin-btn admin-btn-success">
                    <span>📧</span> Enviar Agora
                  </button>
                  <button id="admin-companion-cancel-btn" class="admin-btn admin-btn-warning">
                    <span>❌</span> Cancelar
                  </button>
                  <button id="admin-companion-reset-stop-btn" class="admin-btn admin-btn-secondary">
                    <span>🔄</span> Reset Stop
                  </button>
                  <button id="admin-companion-reset-email-count-btn" class="admin-btn admin-btn-secondary">
                    <span>📊</span> Reset Email Count
                  </button>
                </div>
              </div>
              <!-- Result area with Visual/JSON toggle -->
              <div id="admin-companion-result" class="companion-result-container">
                <div class="companion-result-header">
                  <span class="companion-result-title">Resultado</span>
                  <div class="companion-view-toggle">
                    <button class="toggle-btn active" data-view="visual">Visual</button>
                    <button class="toggle-btn" data-view="json">JSON</button>
                  </div>
                </div>
                <div id="companion-result-visual" class="companion-result-view"></div>
                <div id="companion-result-json" class="companion-result-view hidden"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    // Setup event listeners after rendering
    this.setupAdminEventListeners();
  }

  /**
   * Setup all admin button event listeners
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private setupAdminEventListeners(): void {
    // Refresh button
    document.getElementById('admin-refresh-btn')?.addEventListener('click', () => this.refreshAdminData());

    // Reset buttons
    document.getElementById('admin-reset-all-btn')?.addEventListener('click', () => this.handleAdminResetGames());
    document.getElementById('admin-force-reset-btn')?.addEventListener('click', () => this.handleAdminForceResetGames());
    document.getElementById('admin-clean-finished-btn')?.addEventListener('click', () => this.handleAdminCleanFinishedGames());

    // Create AI game buttons
    document.getElementById('admin-create-ai-2p-btn')?.addEventListener('click', () => this.handleAdminCreateAIGame('individual_2p'));
    document.getElementById('admin-create-ai-4p-btn')?.addEventListener('click', () => this.handleAdminCreateAIGame('partnerships_4p'));

    // Ghost rooms button
    document.getElementById('admin-show-ghosts-btn')?.addEventListener('click', () => this.showGhostRooms());

    // AICompanion test buttons
    document.getElementById('admin-companion-status-btn')?.addEventListener('click', () => this.handleCompanionTestStatus());
    document.getElementById('admin-companion-schedule-btn')?.addEventListener('click', () => this.handleCompanionTestSchedule());
    document.getElementById('admin-companion-send-now-btn')?.addEventListener('click', () => this.handleCompanionTestSendNow());
    document.getElementById('admin-companion-cancel-btn')?.addEventListener('click', () => this.handleCompanionTestCancel());
    document.getElementById('admin-companion-reset-stop-btn')?.addEventListener('click', () => this.handleCompanionTestResetStop());
    document.getElementById('admin-companion-reset-email-count-btn')?.addEventListener('click', () => this.handleCompanionTestResetEmailCount());

    // Visual/JSON toggle buttons
    document.querySelectorAll('.companion-view-toggle .toggle-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const view = target.dataset.view as 'visual' | 'json';
        this.switchCompanionResultView(view);
      });
    });
  }

  /**
   * Switch between visual and JSON view for companion results
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private switchCompanionResultView(view: 'visual' | 'json'): void {
    // Update toggle buttons
    document.querySelectorAll('.companion-view-toggle .toggle-btn').forEach(btn => {
      btn.classList.toggle('active', btn.getAttribute('data-view') === view);
    });

    // Toggle views
    const visualView = document.getElementById('companion-result-visual');
    const jsonView = document.getElementById('companion-result-json');

    if (visualView) visualView.classList.toggle('hidden', view !== 'visual');
    if (jsonView) jsonView.classList.toggle('hidden', view !== 'json');
  }

  /**
   * Refresh admin data (AI games list)
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private async refreshAdminData(): Promise<void> {
    const gamesListEl = document.getElementById('admin-ai-games-list');
    if (!gamesListEl) return;

    gamesListEl.innerHTML = '<div class="admin-loading">Carregando...</div>';

    try {
      const response = await fetch(`${this.serverUrl}/api/lobby/admin/ai-games`);
      const data = await response.json();

      if (data.success && data.games.length > 0) {
        gamesListEl.innerHTML = `
          <div class="admin-games-count">${data.count} jogo(s) ativo(s)</div>
          ${data.games.map((game: GameLobbyEntry) => this.renderAdminGameItem(game)).join('')}
        `;
      } else {
        gamesListEl.innerHTML = `
          <div class="admin-empty-state">
            <span class="empty-icon">✓</span>
            <span>Nenhum jogo de IA ativo</span>
          </div>
        `;
      }
    } catch (error) {
      console.error('🔧 Error fetching AI games:', error);
      gamesListEl.innerHTML = `<div class="admin-error">Erro ao carregar jogos</div>`;
    }
  }

  /**
   * Render a single game item for the admin list
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private renderAdminGameItem(game: GameLobbyEntry): string {
    const statusClass = game.status === 'playing' ? 'status-playing' :
                        game.status === 'finished' ? 'status-finished' : 'status-waiting';
    const statusLabel = game.status === 'playing' ? 'Jogando' :
                        game.status === 'finished' ? 'Finalizado' : 'Aguardando';
    const playerNames = game.players.map(p => p.name).join(', ');
    const typeLabel = this.getGameTypeLabel(game.type);

    return `
      <div class="admin-game-item ${statusClass}">
        <div class="game-item-header">
          <code class="game-id">${game.id}</code>
          <span class="game-type-label">${typeLabel}</span>
          <span class="game-status-badge ${statusClass}">${statusLabel}</span>
        </div>
        <div class="game-item-players">
          <span class="players-icon">👥</span>
          <span>${playerNames || 'Sem jogadores'}</span>
        </div>
      </div>
    `;
  }

  // NOTE: Old renderAdminPanel() removed - replaced by renderAdminScreen() in new tab-based design
  // NOTE: Old renderAdminGameCard() removed - replaced by renderAdminGameItem() in new design
  // NOTE: Old refreshAdminPanel() removed - replaced by refreshAdminData() in new design

  /**
   * Handle reset all AI games button click
   * 
   * DATE: 2025-10-31 | Author: IA + Luiz Couto
   */
  private async handleAdminResetGames(): Promise<void> {
    const confirmed = confirm('Tem certeza que deseja resetar TODOS os jogos de IA?');
    if (!confirmed) return;

    console.log('🔧 [2025-10-31] Resetting all AI games...');

    try {
      const response = await fetch(`${this.serverUrl}/api/lobby/admin/reset-games`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          resetAll: true,
          resetFinished: true
        })
      });

      const data = await response.json();

      if (data.success) {
        console.log('🔧 [2025-10-31] Reset complete:', data);
        this.showSuccess(`✓ ${data.results.success} jogos resetados com sucesso`);
        
        // Refresh admin panel to show updated state
        await this.refreshAdminData();
      } else {
        console.error('🔧 [2025-10-31] Reset failed:', data);
        this.showError(`Erro ao resetar jogos: ${data.error}`);
      }
    } catch (error) {
      console.error('🔧 [2025-10-31] Error resetting games:', error);
      this.showError(`Erro ao resetar jogos: ${error}`);
    }
  }

  /**
   * Handle force reset ALL playing games button click
   * 
   * DATE: 2025-12-20 | Author: IA + Luiz Couto
   * 
   * Uses the "forceAll" option to reset ALL games in "playing" status,
   * ignoring the isAI property check. This is useful when:
   * - Storage has stale data without isAI property
   * - Need to forcefully clear all stuck games
   * - Normal reset isn't detecting AI-only games correctly
   */
  private async handleAdminForceResetGames(): Promise<void> {
    const confirmed = confirm(
      '⚠️ ATENÇÃO: Isso vai resetar TODOS os jogos em andamento!\n\n' +
      'Diferente do reset normal, este comando:\n' +
      '• Ignora verificação de IA (reseta jogos com humanos também)\n' +
      '• Força reset mesmo com dados desatualizados\n\n' +
      'Tem certeza que deseja continuar?'
    );
    if (!confirmed) return;

    console.log('⚡ [2025-12-20] Force resetting ALL playing games...');

    try {
      const response = await fetch(`${this.serverUrl}/api/lobby/admin/reset-games`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          forceAll: true  // DATE: 2025-12-20 | Force reset ignoring isAI check
        })
      });

      const data = await response.json();

      if (data.success) {
        console.log('⚡ [2025-12-20] Force reset complete:', data);
        this.showSuccess(`⚡ ${data.results.success} jogos forçadamente resetados`);
        
        // Refresh admin panel to show updated state
        await this.refreshAdminData();
      } else {
        console.error('⚡ [2025-12-20] Force reset failed:', data);
        this.showError(`Erro ao forçar reset: ${data.error}`);
      }
    } catch (error) {
      console.error('⚡ [2025-12-20] Error force resetting games:', error);
      this.showError(`Erro ao forçar reset: ${error}`);
    }
  }

  /**
   * Handle clean finished games button click
   *
   * DATE: 2025-12-25 | Author: IA + Luiz Couto
   *
   * Cleans up games stuck in "finished" status in the lobby.
   * These are ghost entries that show players but the actual GameRoom is empty.
   */
  private async handleAdminCleanFinishedGames(): Promise<void> {
    const confirmed = confirm(
      '🧹 Limpar jogos finalizados?\n\n' +
      'Isso vai resetar todas as mesas com status "FINALIZADO".\n' +
      'Remove fantasmas que aparecem com jogadores mas estão vazias.\n\n' +
      'Continuar?'
    );
    if (!confirmed) return;

    console.log('🧹 [2025-12-25] Cleaning finished games...');

    try {
      const response = await fetch(`${this.serverUrl}/api/lobby/admin/clean-finished-games`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        console.log('🧹 [2025-12-25] Clean finished complete:', data);
        if (data.results.total === 0) {
          this.showSuccess('🧹 Nenhum jogo finalizado para limpar');
        } else {
          this.showSuccess(`🧹 ${data.results.success} jogos finalizados limpos`);
        }

        // Refresh admin panel to show updated state
        await this.refreshAdminData();
      } else {
        console.error('🧹 [2025-12-25] Clean finished failed:', data);
        this.showError(`Erro ao limpar finalizados: ${data.error}`);
      }
    } catch (error) {
      console.error('🧹 [2025-12-25] Error cleaning finished games:', error);
      this.showError(`Erro ao limpar finalizados: ${error}`);
    }
  }

  /**
   * Handle create AI game button click
   *
   * DATE: 2025-12-20 | Author: IA + Luiz Couto
   *
   * Creates a new game populated entirely with AI players.
   * This is used to create "movement" in the lobby without requiring human players.
   * 
   * @param gameType - Type of game to create ('individual_2p' or 'partnerships_4p')
   */
  private async handleAdminCreateAIGame(gameType: 'individual_2p' | 'partnerships_4p'): Promise<void> {
    const gameTypeName = gameType === 'individual_2p' ? 'Individual 1v1' : 'Duplas 2v2';
    
    console.log(`🤖 [2025-12-20] Creating AI game: ${gameType}`);

    try {
      const response = await fetch(`${this.serverUrl}/api/lobby/admin/create-ai-game`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ gameType })
      });

      const data = await response.json();

      if (data.success) {
        console.log('🤖 [2025-12-20] AI game created:', data);
        this.showSuccess(`🤖 Jogo ${gameTypeName} criado: ${data.gameId}\nJogadores: ${data.players?.join(', ') || 'N/A'}`);
        
        // Refresh admin panel to show the new game
        await this.refreshAdminData();
      } else {
        console.error('🤖 [2025-12-20] Failed to create AI game:', data);
        this.showError(`Erro ao criar jogo de IA: ${data.error}`);
      }
    } catch (error) {
      console.error('🤖 [2025-12-20] Error creating AI game:', error);
      this.showError(`Erro ao criar jogo de IA: ${error}`);
    }
  }

  /**
   * Show ghost rooms section
   *
   * DATE: 2025-10-31 | Author: IA + Luiz Couto
   * DATE: 2025-12-21 | Updated for new admin screen design
   *
   * Fetches and displays ghost rooms (rooms with 'pending' or invalid IDs)
   */
  private async showGhostRooms(): Promise<void> {
    console.log('👻 [2025-12-21] Fetching ghost rooms...');

    // Use new admin screen container
    const ghostResults = document.getElementById('admin-ghost-results');
    if (!ghostResults) return;

    // Show loading
    ghostResults.innerHTML = '<div class="admin-loading">Buscando ghost rooms...</div>';

    try {
      const response = await fetch(`${this.serverUrl}/api/lobby/admin/ghost-rooms`);
      const data = await response.json();

      if (data.success) {
        console.log('👻 [2025-12-21] Ghost rooms found:', data.count);

        if (data.count === 0) {
          ghostResults.innerHTML = `
            <div class="admin-ghost-ok">
              <span class="ghost-ok-icon">✅</span>
              <span>Nenhum ghost room detectado</span>
            </div>
          `;
        } else {
          ghostResults.innerHTML = `
            <div class="admin-ghost-warning">
              <span class="ghost-warning-icon">⚠️</span>
              <span>${data.count} ghost room(s) detectado(s)</span>
            </div>
            <div class="ghost-rooms-list">
              ${data.ghostRooms.map((room: any) => `
                <div class="ghost-room-item">
                  <code>${room.id}</code>
                  <span class="ghost-reason">${room.reason}</span>
                  <span class="ghost-meta">${room.status || 'unknown'} • ${room.players || 0} players</span>
                </div>
              `).join('')}
            </div>
          `;
        }
      } else {
        ghostResults.innerHTML = `<div class="admin-error">Erro ao buscar ghost rooms</div>`;
      }
    } catch (error) {
      console.error('👻 [2025-12-21] Error fetching ghost rooms:', error);
      ghostResults.innerHTML = `<div class="admin-error">Erro: ${error}</div>`;
    }
  }

  // =========================================================================
  // DATE: 2025-12-21 | Author: IA + Luiz Couto
  // AICompanion Test Methods - Re-engagement Email System
  // These methods allow testing the 24h re-engagement email feature
  // without waiting for the actual timeout
  // =========================================================================

  /**
   * Get selected companion name and friend ID from admin form
   */
  private getCompanionTestInputs(): { companionName: string; friendId: number | null; delaySecs: number } {
    const companionSelect = document.getElementById('admin-companion-select') as HTMLSelectElement;
    const friendIdInput = document.getElementById('admin-friend-id') as HTMLInputElement;
    const delayInput = document.getElementById('admin-delay-secs') as HTMLInputElement;

    const companionName = companionSelect?.value || 'Lisa';
    const friendId = friendIdInput?.value ? parseInt(friendIdInput.value) : null;
    const delaySecs = delayInput?.value ? parseInt(delayInput.value) : 60;

    return { companionName, friendId, delaySecs };
  }

  /**
   * Show result in the admin panel result area
   * DATE: 2025-12-21 | Updated with Visual/JSON toggle views
   */
  private showCompanionTestResult(result: any): void {
    const resultContainer = document.getElementById('admin-companion-result');
    const visualView = document.getElementById('companion-result-visual');
    const jsonView = document.getElementById('companion-result-json');

    if (!resultContainer || !visualView || !jsonView) return;

    // Show the result container
    resultContainer.classList.add('visible');

    // Render JSON view
    jsonView.innerHTML = `<pre>${JSON.stringify(result, null, 2)}</pre>`;

    // Render Visual view
    visualView.innerHTML = this.renderCompanionStatusVisual(result);
  }

  /**
   * Render companion status in a visual format
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private renderCompanionStatusVisual(data: any): string {
    // If it's an error or simple response
    if (!data.success) {
      return `
        <div class="status-error">
          <span class="status-icon">❌</span>
          <span>${data.message || data.error || 'Erro desconhecido'}</span>
        </div>
      `;
    }

    // If it's a simple action response (schedule, cancel, etc.)
    if (data.message && !data.pendingEmails) {
      const icon = data.emailSent ? '✅' : data.wasPending !== undefined ? '🗑️' : '✅';
      return `
        <div class="status-success">
          <span class="status-icon">${icon}</span>
          <span>${data.message}</span>
          ${data.taskId ? `<code class="task-id">Task: ${data.taskId}</code>` : ''}
          ${data.scheduledFor ? `<span class="scheduled-time">Dispara em: ${this.formatTimeUntil(data.scheduledFor - Date.now())}</span>` : ''}
        </div>
      `;
    }

    // Full status response - render visual dashboard
    const {
      companionName = 'Unknown',
      companionId = 0,
      weeklyEmailState = { weekStart: '', count: 0, recipients: [] },
      pendingEmails = [],
      stoppedUsers = {},
      lastCheckIn = {}
    } = data;

    // Build pending emails section
    const pendingEmailsHtml = pendingEmails.length > 0
      ? pendingEmails.map((email: any) => `
          <div class="status-item status-item-pending">
            <span class="item-icon">⏰</span>
            <div class="item-content">
              <span class="item-label">Friend #${email.friendId}</span>
              <span class="item-value">${this.formatTimeUntil(email.timeUntilFireMs)}</span>
            </div>
            <code class="item-meta">${email.taskId.slice(0, 8)}...</code>
          </div>
        `).join('')
      : '<div class="status-empty">Nenhum email agendado</div>';

    // Build last check-in section
    const checkInEntries = Object.entries(lastCheckIn);
    const lastCheckInHtml = checkInEntries.length > 0
      ? checkInEntries.map(([friendId, date]) => `
          <div class="status-item">
            <span class="item-icon">💬</span>
            <div class="item-content">
              <span class="item-label">Friend #${friendId}</span>
              <span class="item-value">${date}</span>
            </div>
          </div>
        `).join('')
      : '<div class="status-empty">Nenhum check-in registrado</div>';

    // Build stopped users section
    const stoppedEntries = Object.entries(stoppedUsers);
    const stoppedUsersHtml = stoppedEntries.length > 0
      ? stoppedEntries.map(([friendId, reason]: [string, any]) => `
          <div class="status-item status-item-stopped">
            <span class="item-icon">🚫</span>
            <div class="item-content">
              <span class="item-label">Friend #${friendId}</span>
              <span class="item-value">${reason.reason || 'Parado'}</span>
            </div>
          </div>
        `).join('')
      : '<div class="status-empty">Nenhum usuário parado</div>';

    return `
      <div class="companion-status-dashboard">
        <!-- Header with companion info -->
        <div class="status-header">
          <div class="companion-badge">
            <span class="companion-avatar">🤖</span>
            <div class="companion-info">
              <span class="companion-name">${companionName}</span>
              <code class="companion-id">#${companionId}</code>
            </div>
          </div>
          <div class="week-stats">
            <span class="week-label">Emails esta semana</span>
            <span class="week-count">${weeklyEmailState.count}/2</span>
          </div>
        </div>

        <!-- Status sections -->
        <div class="status-sections">
          <!-- Pending Emails -->
          <div class="status-section">
            <div class="section-header">
              <span class="section-icon">📧</span>
              <span class="section-title">Emails Agendados</span>
              <span class="section-count">${pendingEmails.length}</span>
            </div>
            <div class="section-content">
              ${pendingEmailsHtml}
            </div>
          </div>

          <!-- Last Check-ins -->
          <div class="status-section">
            <div class="section-header">
              <span class="section-icon">💬</span>
              <span class="section-title">Últimos Check-ins</span>
              <span class="section-count">${checkInEntries.length}</span>
            </div>
            <div class="section-content">
              ${lastCheckInHtml}
            </div>
          </div>

          <!-- Stopped Users -->
          <div class="status-section">
            <div class="section-header">
              <span class="section-icon">🚫</span>
              <span class="section-title">Usuários Parados</span>
              <span class="section-count">${stoppedEntries.length}</span>
            </div>
            <div class="section-content">
              ${stoppedUsersHtml}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Format milliseconds to human-readable time
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private formatTimeUntil(ms: number): string {
    if (ms <= 0) return 'Agora';

    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      return `${hours}h ${remainingMinutes}m`;
    }
    if (minutes > 0) {
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  }

  /**
   * Handle get AICompanion status button click
   *
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private async handleCompanionTestStatus(): Promise<void> {
    const { companionName, friendId } = this.getCompanionTestInputs();

    console.log(`📊 [2025-12-21] Getting status for AICompanion ${companionName}`);

    try {
      let url = `${this.serverUrl}/api/companion/${encodeURIComponent(companionName)}/test/status`;
      if (friendId) {
        url += `?friendId=${friendId}`;
      }

      const response = await fetch(url);
      const data = await response.json();

      console.log('📊 [2025-12-21] Status result:', data);
      this.showCompanionTestResult(data);

      if (data.success) {
        this.showSuccess(`Status de ${companionName} obtido`);
      } else {
        this.showError(`Erro: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('📊 [2025-12-21] Error getting status:', error);
      this.showError(`Erro ao obter status: ${error}`);
    }
  }

  /**
   * Handle schedule re-engagement email button click
   *
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private async handleCompanionTestSchedule(): Promise<void> {
    const { companionName, friendId, delaySecs } = this.getCompanionTestInputs();

    if (!friendId) {
      this.showError('Por favor, informe o Friend ID');
      return;
    }

    console.log(`⏰ [2025-12-21] Scheduling email via ${companionName} for friend ${friendId} in ${delaySecs}s`);

    try {
      const response = await fetch(`${this.serverUrl}/api/companion/${encodeURIComponent(companionName)}/test/schedule-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ friendId, delaySecs })
      });

      const data = await response.json();

      console.log('⏰ [2025-12-21] Schedule result:', data);
      this.showCompanionTestResult(data);

      if (data.success) {
        this.showSuccess(`Email agendado em ${delaySecs}s`);
      } else {
        this.showError(`Erro: ${data.message || data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('⏰ [2025-12-21] Error scheduling email:', error);
      this.showError(`Erro ao agendar email: ${error}`);
    }
  }

  /**
   * Handle send email now button click
   *
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private async handleCompanionTestSendNow(): Promise<void> {
    const { companionName, friendId } = this.getCompanionTestInputs();

    if (!friendId) {
      this.showError('Por favor, informe o Friend ID');
      return;
    }

    console.log(`📧 [2025-12-21] Sending email NOW via ${companionName} to friend ${friendId}`);

    try {
      const response = await fetch(`${this.serverUrl}/api/companion/${encodeURIComponent(companionName)}/test/send-email-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ friendId })
      });

      const data = await response.json();

      console.log('📧 [2025-12-21] Send now result:', data);
      this.showCompanionTestResult(data);

      if (data.success && data.emailSent) {
        this.showSuccess(`Email enviado para friend ${friendId}`);
      } else if (data.success) {
        this.showError(`Email não enviado: ${data.message}`);
      } else {
        this.showError(`Erro: ${data.message || data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('📧 [2025-12-21] Error sending email:', error);
      this.showError(`Erro ao enviar email: ${error}`);
    }
  }

  /**
   * Handle cancel pending email button click
   *
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private async handleCompanionTestCancel(): Promise<void> {
    const { companionName, friendId } = this.getCompanionTestInputs();

    if (!friendId) {
      this.showError('Por favor, informe o Friend ID');
      return;
    }

    console.log(`❌ [2025-12-21] Cancelling email via ${companionName} for friend ${friendId}`);

    try {
      const response = await fetch(`${this.serverUrl}/api/companion/${encodeURIComponent(companionName)}/test/cancel-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ friendId })
      });

      const data = await response.json();

      console.log('❌ [2025-12-21] Cancel result:', data);
      this.showCompanionTestResult(data);

      if (data.success) {
        if (data.wasPending) {
          this.showSuccess(`Email cancelado para friend ${friendId}`);
        } else {
          this.showSuccess(`Nenhum email pendente para friend ${friendId}`);
        }
      } else {
        this.showError(`Erro: ${data.message || data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ [2025-12-21] Error cancelling email:', error);
      this.showError(`Erro ao cancelar email: ${error}`);
    }
  }

  /**
   * Handle reset user stop status button click
   *
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private async handleCompanionTestResetStop(): Promise<void> {
    const { companionName, friendId } = this.getCompanionTestInputs();

    if (!friendId) {
      this.showError('Por favor, informe o Friend ID');
      return;
    }

    console.log(`🔄 [2025-12-21] Resetting stop via ${companionName} for friend ${friendId}`);

    try {
      const response = await fetch(`${this.serverUrl}/api/companion/${encodeURIComponent(companionName)}/test/reset-stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ friendId })
      });

      const data = await response.json();

      console.log('🔄 [2025-12-21] Reset stop result:', data);
      this.showCompanionTestResult(data);

      if (data.success) {
        if (data.wasStoppedBefore) {
          this.showSuccess(`Follow-ups re-habilitados para friend ${friendId}`);
        } else {
          this.showSuccess(`Friend ${friendId} não estava parado`);
        }
      } else {
        this.showError(`Erro: ${data.message || data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('🔄 [2025-12-21] Error resetting stop:', error);
      this.showError(`Erro ao resetar stop: ${error}`);
    }
  }

  /**
   * Handle reset weekly email count button click
   *
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private async handleCompanionTestResetEmailCount(): Promise<void> {
    const { companionName, friendId } = this.getCompanionTestInputs();

    // friendId is optional for this endpoint - if not provided, resets all users
    console.log(`📊 [2025-12-21] Resetting email count via ${companionName}${friendId ? ` for friend ${friendId}` : ' (all users)'}`);

    try {
      const response = await fetch(`${this.serverUrl}/api/companion/${encodeURIComponent(companionName)}/test/reset-email-count`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ friendId: friendId || undefined })
      });

      const data = await response.json();

      console.log('📊 [2025-12-21] Reset email count result:', data);
      this.showCompanionTestResult(data);

      if (data.success) {
        if (friendId) {
          this.showSuccess(`Contador de emails resetado para friend ${friendId} (era ${data.previousCount})`);
        } else {
          this.showSuccess(`Contador de emails resetado para todos (total era ${data.previousCount})`);
        }
      } else {
        this.showError(`Erro: ${data.message || data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('📊 [2025-12-21] Error resetting email count:', error);
      this.showError(`Erro ao resetar contador: ${error}`);
    }
  }
}
